/**
 * Service für die Verwaltung von Portal-Authentifizierungstoken
 *
 * Koordiniert Token-Initialisierung und -Abruf beim Ausführen innerhalb des Portals.
 * Fungiert als Fassade für den AdnovaTokenService, verwaltet dessen Lebenszyklus
 * und stellt Token-Zugriff für authentifizierte API-Anfragen bereit.
 *
 * @class PortalTokenService
 */
import { Injectable, inject } from '@angular/core';
import { NGXLogger } from 'ngx-logger';

import { AdnovaTokenService } from './adnova-token.service';
import { PortalMessageBroker } from './portal-message-broker';

@Injectable({
  providedIn: 'root',
})
export class PortalTokenService {
  private readonly _logger = inject(NGXLogger);
  private readonly _portalMessageBroker = inject(PortalMessageBroker);
  private readonly _adnovaTokenService = inject(AdnovaTokenService);

  /**
   * Flag das anzeigt ob Service initialisiert ist
   */
  private _inited = false;

  /**
   * Flag das anzeigt ob Anwendung im Portal läuft
   */
  private _runningInPortal = false;

  /**
   * Initialisiert den Portal Token Service
   *
   * Startet Token-Refresh wenn im Portal ausgeführt. Sollte einmal während
   * der Anwendungsinitialisierung aufgerufen werden. Nachfolgende Aufrufe werden mit Warnung ignoriert.
   *
   * @param {number} renewTimeBeforeTokenExpiresInSeconds Zeit in Sekunden vor Token-Ablauf für Erneuerungsanfrage
   * @returns {Promise<void>} Promise das aufgelöst wird wenn Initialisierung abgeschlossen ist
   */
  public init(renewTimeBeforeTokenExpiresInSeconds: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!this._inited) {
        this._runningInPortal = this._portalMessageBroker.isRunningInPortal();
        if (this._runningInPortal) {
          this._adnovaTokenService
            .startTokenRefresh(5, renewTimeBeforeTokenExpiresInSeconds)
            .then(() => resolve())
            .catch(reject);
        } else {
          resolve();
        }

        this._inited = true;
      } else {
        this._logger.warn('PortalMessageBroker.init(): already inited, ignoring... ');
        resolve();
      }
    });
  }

  /**
   * Gibt den aktuellen ADNOVA-Authentifizierungstoken zurück
   *
   * @returns {string} Der aktuelle ADNOVA JWT Token
   * @throws {Error} Wenn Token-Refresh nicht läuft oder Token nicht vorhanden ist
   */
  public getAdnovaToken(): string {
    return this._adnovaTokenService.getToken();
  }
}
